import { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";

import * as actions from "../../store/actions/index";
import {
  makeGetApiCall,
  makePostApiCall,
} from "../../shared/makeApiCall";
import { withRouter } from "../../shared/utility";

const today = () => {
  const current = new Date();
  const offset = current.getTimezoneOffset();

  return new Date(current.getTime() - offset * 60 * 1000)
    .toISOString()
    .slice(0, 10);
};

const statusLabel = {
  not_adjusted: "Not synced",
  pending: "Syncing",
  completed: "Synced",
  failed: "Failed",
};

function PackagingInventoryAdjustment({ logout, addSnackbar }) {
  const [selectedDate, setSelectedDate] = useState(today());
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingSku, setSavingSku] = useState(null);

  const notify = useCallback(
    (message, type = "success") => {
      addSnackbar({ id: Date.now(), message, type });
    },
    [addSnackbar],
  );

  const loadPackagings = useCallback(async () => {
    setLoading(true);
    const result = await makeGetApiCall(
      `packagingInventoryAdjustments?date=${encodeURIComponent(selectedDate)}`,
    );

    if (result.success) {
      setItems(
        result.data.items.map((item) => ({
          ...item,
          quantity_made: String(item.quantity_made),
        })),
      );
    } else if (result.errorStatus === 401) {
      logout();
    } else {
      notify(
        result.data?.message || "The packaging records could not be loaded.",
        "error",
      );
      setItems([]);
    }

    setLoading(false);
  }, [logout, notify, selectedDate]);

  useEffect(() => {
    loadPackagings();
  }, [loadPackagings]);

  const updateQuantity = (sku, value) => {
    if (value !== "" && !/^\d+$/.test(value)) return;

    setItems((currentItems) =>
      currentItems.map((item) =>
        item.sku === sku ? { ...item, quantity_made: value } : item,
      ),
    );
  };

  const saveQuantity = async (item) => {
    if (item.quantity_made === "") {
      notify("Enter a quantity made before saving.", "warn");
      return;
    }

    setSavingSku(item.sku);
    const result = await makePostApiCall("packagingInventoryAdjustments", {
      date: selectedDate,
      sku: item.sku,
      quantity_made: Number(item.quantity_made),
    });

    if (result.success) {
      notify(result.data.message);
      await loadPackagings();
    } else if (result.errorStatus === 401) {
      logout();
    } else {
      notify(
        result.data?.message ||
          "The packaging quantity could not be updated in QuickBooks.",
        "error",
      );
    }

    setSavingSku(null);
  };

  return (
    <div className="mt-2 px-4 sm:px-6 lg:px-8">
      <div className="card-custom px-4 py-5 sm:px-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-lg font-semibold leading-6 text-gray-900">
              Packaging Inventory Adjustment
            </h1>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">
              Review finished quantities for one packaging day and synchronize
              them to QuickBooks by SKU.
            </p>
          </div>
          <div className="w-full sm:w-64">
            <label
              htmlFor="packaging-adjustment-date"
              className="block text-sm font-medium text-gray-700"
            >
              Packaging date
            </label>
            <input
              id="packaging-adjustment-date"
              type="date"
              value={selectedDate}
              onChange={(event) => setSelectedDate(event.target.value)}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-main-purple focus:outline-none focus:ring-1 focus:ring-main-purple"
            />
          </div>
        </div>
      </div>

      <div className="mt-6 overflow-hidden card-custom">
        {loading ? (
          <div className="p-6 text-sm text-gray-500">Loading packagings…</div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center">
            <p className="text-sm font-medium text-gray-900">
              No packaging records found
            </p>
            <p className="mt-1 text-sm text-gray-500">
              Choose another date to review its finished quantities.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-300">
              <thead className="bg-white">
                <tr>
                  <th className="py-3 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                    SKU
                  </th>
                  <th className="px-3 py-3 text-left text-sm font-semibold text-gray-900">
                    Product
                  </th>
                  <th className="px-3 py-3 text-right text-sm font-semibold text-gray-900">
                    Packaging records
                  </th>
                  <th className="px-3 py-3 text-right text-sm font-semibold text-gray-900">
                    Quantity made
                  </th>
                  <th className="px-3 py-3 text-left text-sm font-semibold text-gray-900">
                    QBO status
                  </th>
                  <th className="relative py-3 pl-3 pr-4 sm:pr-6">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {items.map((item) => {
                  const isSaving = savingSku === item.sku;
                  const savedQuantity =
                    item.synced_quantity === null
                      ? null
                      : Number(item.synced_quantity);
                  const hasChanged =
                    item.quantity_made !== "" &&
                    Number(item.quantity_made) !== savedQuantity;

                  return (
                    <tr key={item.sku}>
                      <td className="whitespace-nowrap py-3 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                        {item.sku}
                      </td>
                      <td className="px-3 py-3 text-sm text-gray-600">
                        <div className="font-medium text-gray-900">
                          {item.product_name || "Unnamed product"}
                        </div>
                        {item.description && (
                          <div className="mt-0.5 text-xs text-gray-500">
                            {item.description}
                          </div>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-3 py-3 text-right text-sm text-gray-600">
                        {item.source_quantity}
                      </td>
                      <td className="whitespace-nowrap px-3 py-3 text-right">
                        <input
                          type="number"
                          min="0"
                          step="1"
                          aria-label={`Quantity made for ${item.sku}`}
                          value={item.quantity_made}
                          onChange={(event) =>
                            updateQuantity(item.sku, event.target.value)
                          }
                          className="w-28 rounded-md border border-gray-300 px-3 py-2 text-right text-sm shadow-sm focus:border-main-purple focus:outline-none focus:ring-1 focus:ring-main-purple"
                        />
                      </td>
                      <td className="px-3 py-3 text-sm">
                        <span
                          className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${
                            item.status === "completed"
                              ? "bg-green-100 text-green-700"
                              : item.status === "failed"
                                ? "bg-red-100 text-red-700"
                                : "bg-yellow-100 text-yellow-700"
                          }`}
                        >
                          {statusLabel[item.status] || item.status}
                        </span>
                        {item.status === "failed" && item.error && (
                          <p className="mt-1 max-w-xs text-xs text-red-600">
                            {item.error}
                          </p>
                        )}
                      </td>
                      <td className="whitespace-nowrap py-3 pl-3 pr-4 text-right text-sm sm:pr-6">
                        <button
                          type="button"
                          disabled={
                            isSaving ||
                            savingSku !== null ||
                            item.quantity_made === "" ||
                            (!hasChanged && item.status === "completed")
                          }
                          onClick={() => saveQuantity(item)}
                          className="rounded-md bg-main-purple px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-600 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {isSaving
                            ? "Updating…"
                            : item.status === "completed"
                              ? "Update QBO"
                              : "Sync to QBO"}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const mapDispatchToProps = (dispatch) => ({
  logout: () => dispatch(actions.logout()),
  addSnackbar: (snackbar) => dispatch(actions.addSnackbar(snackbar)),
});

export default withRouter(
  connect(null, mapDispatchToProps)(PackagingInventoryAdjustment),
);
